import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import {
  HiOutlineSearch,
  HiOutlineUserGroup,
  HiOutlineUsers,
  HiOutlineUser,
  HiX,
} from "react-icons/hi";
import UserAvatar from "../../../atoms/UserAvatar";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetSearchUsers } from "../../../../hooks/users";
import { PERSON_SEARCH_MIN_LENGTH } from "../../../molecules/PersonSearch";
import { useGetGroups } from "../../../../hooks/groups";
import {
  useDashboardAccessByRole,
  isTeamMemberRole,
} from "../../../../hooks/roles";
import { MAX_TODO_LINKS } from "../../../../types/TodoTypes";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

export interface PickedUser {
  id: string;
  name: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role?: string;
}

export interface PickedGroup {
  id: string;
  name: string;
}

interface Props {
  customerId: string;
  entityId: string;
  userIds: string[];
  groupIds: string[];
  resolvedUsers: Record<string, PickedUser>;
  resolvedGroups: Record<string, PickedGroup>;
  onChangeUsers: (ids: string[]) => void;
  onChangeGroups: (ids: string[]) => void;
  allowedTeamUserIds?: string[];
  allowGroups?: boolean;
  splitByRole?: boolean;
  resolving?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Shown on a linked person's row, before the button that removes them. */
  userAction?: (id: string, user?: PickedUser) => React.ReactNode;
  /** A linked person who cannot be removed: their row has no button for it. */
  lockedUserId?: string | null;
}

export const mapSearchUser = (u: any): PickedUser => ({
  id: u.user_identification,
  firstName: u.user_first_name,
  lastName: u.user_last_name,
  email: u.user_email,
  avatar: u.user_avatar || undefined,
  name:
    [u.user_first_name, u.user_last_name].filter(Boolean).join(" ") ||
    u.user_user_name ||
    u.user_email ||
    u.user_identification,
  role: u.user_role,
});

const AssigneePicker: React.FC<Props> = ({
  customerId,
  entityId,
  userIds,
  groupIds,
  resolvedUsers,
  resolvedGroups,
  onChangeUsers,
  onChangeGroups,
  allowedTeamUserIds,
  allowGroups = true,
  splitByRole = true,
  resolving = false,
  searchPlaceholder,
  emptyText,
  disabled = false,
  userAction,
  lockedUserId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canReadGroups =
    allowGroups && permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW);
  const canSearch = permissions.can(Cap.SEARCH, Level.VIEW);
  const { data: accessByRole } = useDashboardAccessByRole();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showGroups, setShowGroups] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchCache, setSearchCache] = useState<Record<string, PickedUser>>(
    {}
  );

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (trimmed.length < PERSON_SEARCH_MIN_LENGTH) {
      setSearchError(t("dashboard.dlSearch.minLength") as string);
      return;
    }
    setSearchError("");
    setSearchQuery(trimmed);
  };

  // The picker sits inside the appointment's form: Enter must not submit it.
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleSearch();
  };

  const { data: searchData, isFetching } = useGetSearchUsers({
    customer_identification: customerId,
    entityIdentification: entityId,
    query: searchQuery,
    limit: 8,
    pageParam: 0,
  });

  const allSearchUsers: PickedUser[] = useMemo(() => {
    const raw = Array.isArray(searchData)
      ? searchData
      : searchData?.users ?? [];
    return raw.map(mapSearchUser).filter((u: PickedUser) => u.id);
  }, [searchData]);

  const isAssignable = useCallback(
    (u: PickedUser) => {
      if (!allowedTeamUserIds) return true;
      if (!isTeamMemberRole(u.role, accessByRole)) return true;
      return allowedTeamUserIds.includes(u.id);
    },
    [allowedTeamUserIds, accessByRole]
  );

  const results = useMemo(
    () =>
      allSearchUsers.filter((u) => !userIds.includes(u.id) && isAssignable(u)),
    [allSearchUsers, userIds, isAssignable]
  );

  useEffect(() => {
    if (allSearchUsers.length === 0) return;
    setSearchCache((prev) => {
      const next = { ...prev };
      let changed = false;
      allSearchUsers.forEach((u) => {
        if (u.name && next[u.id]?.name !== u.name) {
          next[u.id] = u;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [allSearchUsers]);

  const userInfo = (id: string): PickedUser | undefined => {
    const fromParent = resolvedUsers[id];
    if (fromParent?.name) return fromParent;
    return searchCache[id] || fromParent;
  };

  const { data: groupsData } = useGetGroups({
    customer_identification: customerId,
    entityIdentification: entityId,
    limit: 100,
    pageParam: 0,
    sortOrder: "ASC",
    enabled: canReadGroups,
  });

  const groupCache: Record<string, PickedGroup> = useMemo(() => {
    const raw = Array.isArray(groupsData)
      ? groupsData
      : groupsData?.groups ?? [];
    const map: Record<string, PickedGroup> = {};
    raw.forEach((g: any) => {
      if (g.group_identification)
        map[g.group_identification] = {
          id: g.group_identification,
          name: g.group_name,
        };
    });
    return map;
  }, [groupsData]);

  const groupInfo = (id: string): PickedGroup | undefined => {
    const fromParent = resolvedGroups[id];
    if (fromParent?.name) return fromParent;
    return groupCache[id] || fromParent;
  };

  const availableGroups: PickedGroup[] = useMemo(() => {
    return Object.values(groupCache).filter(
      (g) => g.id && !groupIds.includes(g.id)
    );
  }, [groupCache, groupIds]);

  const { team, customers } = useMemo(() => {
    const team: string[] = [];
    const customers: string[] = [];
    userIds.forEach((id) => {
      if (isTeamMemberRole(userInfo(id)?.role, accessByRole)) team.push(id);
      else customers.push(id);
    });
    return { team, customers };
  }, [userIds, resolvedUsers, searchCache, accessByRole]);

  const atLinkCap = userIds.length + groupIds.length >= MAX_TODO_LINKS;
  const totalLinked = userIds.length + groupIds.length;

  const addUser = (u: PickedUser) => {
    if (atLinkCap || userIds.includes(u.id)) return;
    onChangeUsers([...userIds, u.id]);
    setSearchInput("");
    setSearchQuery("");
  };
  const removeUser = (id: string) =>
    onChangeUsers(userIds.filter((x) => x !== id));

  const addGroup = (g: PickedGroup) => {
    if (atLinkCap || groupIds.includes(g.id)) return;
    onChangeGroups([...groupIds, g.id]);
    setShowGroups(false);
  };
  const removeGroup = (id: string) =>
    onChangeGroups(groupIds.filter((x) => x !== id));

  const personRow = (id: string) => {
    const u = userInfo(id);
    return (
      <div
        key={id}
        className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2 hover:border-gray-200"
      >
        <UserAvatar
          firstName={u?.firstName}
          lastName={u?.lastName}
          email={u?.email}
          avatar={u?.avatar}
          sizeClass="w-8 h-8"
          textClass="text-xs"
        />
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-medium truncate ${
              u?.name ? "text-gray-800" : "text-gray-400 italic"
            }`}
          >
            {u?.name ||
              (resolving
                ? t("todos.filters.resolving")
                : t("todos.task.unknownUser"))}
          </div>
          {u?.email && (
            <div className="text-xs text-gray-400 truncate">{u.email}</div>
          )}
        </div>
        {userAction?.(id, u)}
        {!disabled && id !== lockedUserId && (
          <CustomButton
            type="button"
            variant="none"
            size="icon"
            onClick={() => removeUser(id)}
            className="shrink-0 rounded-md text-gray-400 enabled:hover:text-red-600"
            icon={<HiX size={16} />}
            aria-label={t("todos.task.removeAssignee") as string}
          />
        )}
      </div>
    );
  };

  const sectionHeader = (
    icon: React.ReactNode,
    label: string,
    count: number
  ) => (
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5 mt-4 first:mt-0">
      {icon}
      <span>{label}</span>
      <span className="text-gray-300">({count})</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {!disabled && !atLinkCap && !canSearch && (
        <p className="text-sm text-gray-600">
          {t("agenda.types.drawer.searchNeeded")}
        </p>
      )}
      {!disabled && !atLinkCap && canSearch && (
        <div>
          <div className="flex gap-2">
            <div className="flex-1">
              <TextInput
                type="text"
                icon={HiOutlineSearch}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (searchError) setSearchError("");
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder={
                  searchPlaceholder ??
                  (t("todos.task.searchUserPlaceholder") as string)
                }
                color={searchError ? "failure" : undefined}
                className="w-full"
              />
            </div>
            <CustomButton
              title={t("common.search") as string}
              onClick={handleSearch}
              variant="primary"
              disabled={isFetching}
              isLoading={isFetching}
              /*
               * Plus de `max-h-[44px]`: le meme couple champ + bouton portait
               * ici 44px et 42px dans la modale de signature, deux plafonds
               * ecrits a la main pour la meme intention. La ligne etire deja
               * le bouton a la hauteur du champ.
               */
            />
          </div>
          {searchError && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {searchError}
            </p>
          )}
          {allowedTeamUserIds && (
            <p className="mt-1 text-xs text-gray-500">
              {t("todos.access.assignRestrictedHint")}
            </p>
          )}
          {searchQuery.trim().length >= PERSON_SEARCH_MIN_LENGTH && (
            <ul className="mt-1 max-h-52 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 shadow-sm">
              {isFetching && (
                <li className="px-3 py-2 text-xs text-gray-400">
                  {t("todos.task.searching")}
                </li>
              )}
              {!isFetching && searchData?.tooManyResults && (
                <li className="px-3 py-2 text-xs text-gray-500">
                  <span className="block font-medium text-gray-700">
                    {t("dashboard.searchPage.tooMany.title")}
                  </span>
                  {t("dashboard.searchPage.tooMany.description")}
                </li>
              )}
              {!isFetching &&
                !searchData?.tooManyResults &&
                results.length === 0 && (
                  <li className="px-3 py-2 text-xs text-gray-400">
                    {t("todos.task.noUsers")}
                  </li>
                )}
              {results.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => addUser(u)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50"
                  >
                    <UserAvatar
                      firstName={u.firstName}
                      lastName={u.lastName}
                      email={u.email}
                      avatar={u.avatar}
                      sizeClass="w-8 h-8"
                      textClass="text-xs"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-800 truncate">
                        {u.name}
                      </span>
                      {u.email && (
                        <span className="block text-xs text-gray-400 truncate">
                          {u.email}
                        </span>
                      )}
                    </span>
                    {splitByRole && isTeamMemberRole(u.role, accessByRole) && (
                      <span className="text-xs uppercase tracking-wide font-semibold text-blue-500 flex-shrink-0">
                        {t("todos.task.operatorTag")}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!disabled && !atLinkCap && allowGroups && (
        <div>
          <button
            type="button"
            onClick={() => setShowGroups((s) => !s)}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
          >
            <HiOutlineUserGroup size={16} />
            {showGroups
              ? t("todos.task.hideGroups")
              : t("todos.task.linkGroup")}
          </button>
          {showGroups && (
            <ul className="mt-1 max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 shadow-sm">
              {availableGroups.length === 0 && (
                <li className="px-3 py-2 text-xs text-gray-400">
                  {t("todos.task.noGroups")}
                </li>
              )}
              {availableGroups.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => addGroup(g)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-amber-50"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                      <HiOutlineUserGroup
                        size={16}
                        className="text-amber-500"
                      />
                    </div>
                    <span className="text-sm text-gray-800 truncate">
                      {g.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {atLinkCap && (
        <p className="text-xs text-gray-400">
          {t("todos.task.linkCapReached", { max: MAX_TODO_LINKS })}
        </p>
      )}

      {totalLinked === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          {emptyText ?? t("todos.task.noneLinked")}
        </p>
      ) : (
        <div>
          {!splitByRole && (
            <div className="space-y-1.5">{userIds.map(personRow)}</div>
          )}
          {splitByRole && team.length > 0 && (
            <>
              {sectionHeader(
                <HiOutlineUsers size={13} className="text-blue-500" />,
                t("todos.task.sectionTeam") as string,
                team.length
              )}
              <div className="space-y-1.5">{team.map(personRow)}</div>
            </>
          )}
          {splitByRole && customers.length > 0 && (
            <>
              {sectionHeader(
                <HiOutlineUser size={13} className="text-gray-500" />,
                t("todos.task.sectionCustomers") as string,
                customers.length
              )}
              <div className="space-y-1.5">{customers.map(personRow)}</div>
            </>
          )}
          {groupIds.length > 0 && (
            <>
              {sectionHeader(
                <HiOutlineUserGroup size={13} className="text-amber-500" />,
                t("todos.task.sectionGroups") as string,
                groupIds.length
              )}
              <div className="space-y-1.5">
                {groupIds.map((id) => {
                  const g = groupInfo(id);
                  return (
                    <div
                      key={id}
                      className="flex items-center gap-3 rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2"
                    >
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                        <HiOutlineUserGroup
                          size={16}
                          className="text-amber-600"
                        />
                      </div>
                      <div
                        className={`min-w-0 flex-1 text-sm font-medium truncate ${
                          g?.name ? "text-gray-800" : "text-gray-400 italic"
                        }`}
                      >
                        {g?.name || t("todos.task.unknownGroup")}
                      </div>
                      {!disabled && (
                        <CustomButton
                          type="button"
                          variant="none"
                          size="icon"
                          onClick={() => removeGroup(id)}
                          className="shrink-0 rounded-md text-gray-400 enabled:hover:text-red-600"
                          icon={<HiX size={16} />}
                          aria-label={t("todos.task.removeAssignee") as string}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AssigneePicker;
